import { __decorate } from "tslib";
import { memoized } from "@buildwithflux/shared";
import { capitalize, mapValues, max, values } from "lodash";
import { DistributorInfo } from "../../helpers/distributorNames";
import { compactNumberFormatter, formatPartPrice, humanizeLifecycleStatus, normalizeLifecycleStatus, } from "../formatters";
import { PricingRecord } from "./base";
export class MPNRecord extends PricingRecord {
    get stockInfoUpdatedAt() {
        return max(Object.values(this.distributorPrices).map((dp) => {
            const timestamp = dp.data.timestamp;
            if (timestamp) {
                // NOTE: timestamp type can be either Date or Firestore
                // Timestamp, although when reading, it is always a Timestamp.
                return "toDate" in timestamp ? timestamp.toDate() : timestamp;
            }
            return null;
        }));
    }
    get minPrice() {
        const min = Math.min(...Object.values(this.distributorPrices).map((p) => p.minPrice));
        if (min >= 0) {
            return min;
        }
        else {
            return 0;
        }
    }
    get formattedMinPrice() {
        return formatPartPrice(this.minPrice);
    }
    get maxPrice() {
        const max = Math.max(...Object.values(this.distributorPrices).map((p) => p.maxPrice));
        if (max >= 0) {
            return max;
        }
        else {
            return 0;
        }
    }
    get averagePrice() {
        return (this.minPrice + this.maxPrice) / 2;
    }
    get formattedMaxPrice() {
        return formatPartPrice(this.maxPrice);
    }
    get formattedAveragePrice() {
        return `~${formatPartPrice(this.averagePrice)}`;
    }
    get formattedPriceRange() {
        if (isNaN(this.minPrice) || isNaN(this.maxPrice))
            return "N/A";
        if (this.minPrice === this.maxPrice) {
            return this.formattedMinPrice;
        }
        return `${this.formattedMinPrice}–${this.formattedMaxPrice}`;
    }
    get minStock() {
        const min = Math.min(...Object.values(this.distributorPrices).map((p) => p.minStock));
        if (min >= 0) {
            return min;
        }
        else {
            return 0;
        }
    }
    get formattedMinStock() {
        return compactNumberFormatter.format(this.minStock);
    }
    get maxStock() {
        const max = Math.max(...Object.values(this.distributorPrices).map((p) => p.maxStock));
        if (max >= 0) {
            return max;
        }
        else {
            return 0;
        }
    }
    get formattedMaxStock() {
        return compactNumberFormatter.format(this.maxStock);
    }
    get formattedStockRange() {
        if (isNaN(this.minStock) || isNaN(this.maxStock))
            return "N/A";
        if (this.minStock === this.maxStock) {
            return this.formattedMinStock;
        }
        return `${this.formattedMinStock}–${this.formattedMaxStock}`;
    }
    get lifeCycleState() {
        if (values(this.distributorPrices).find((dp) => dp.lifeCycleStatus === "end_of_life")) {
            return "error";
        }
        else if (values(this.distributorPrices).find((dp) => dp.lifeCycleStatus === "not_recommended_for_new_designs")) {
            return "warning";
        }
    }
    get humanizedLifeCycleState() {
        if (this.lifeCycleState === "error") {
            return "End of Life";
        }
        else if (this.lifeCycleState === "warning") {
            return "Not Recommended for New Designs";
        }
    }
    get skuCount() {
        return this.entry?.skuCount ?? 0;
    }
    get skuIds() {
        return this.entry?.skuIds ?? null;
    }
    get distributorPrices() {
        const prices = this.entry?.distributorPrices ?? {};
        return mapValues(prices, (price, distributor) => {
            return new DistributorPriceSummary(distributor, price);
        });
    }
}
__decorate([
    memoized
], MPNRecord.prototype, "stockInfoUpdatedAt", null);
__decorate([
    memoized
], MPNRecord.prototype, "minPrice", null);
__decorate([
    memoized
], MPNRecord.prototype, "formattedMinPrice", null);
__decorate([
    memoized
], MPNRecord.prototype, "maxPrice", null);
__decorate([
    memoized
], MPNRecord.prototype, "averagePrice", null);
__decorate([
    memoized
], MPNRecord.prototype, "formattedMaxPrice", null);
__decorate([
    memoized
], MPNRecord.prototype, "formattedAveragePrice", null);
__decorate([
    memoized
], MPNRecord.prototype, "formattedPriceRange", null);
__decorate([
    memoized
], MPNRecord.prototype, "minStock", null);
__decorate([
    memoized
], MPNRecord.prototype, "formattedMinStock", null);
__decorate([
    memoized
], MPNRecord.prototype, "maxStock", null);
__decorate([
    memoized
], MPNRecord.prototype, "formattedMaxStock", null);
__decorate([
    memoized
], MPNRecord.prototype, "formattedStockRange", null);
export class NullMPNRecord extends MPNRecord {
    constructor(id = "") {
        super(id, null);
    }
}
export class DistributorPriceSummary {
    distributorIdentifier;
    data;
    constructor(distributorIdentifier, data) {
        this.distributorIdentifier = distributorIdentifier;
        this.data = data;
    }
    get shortPriceSummary() {
        return `${this.distributorName} ${this.formattedPriceRange}`;
    }
    get shortStockSummary() {
        return `${this.distributorName} ${this.formattedStockRange}`;
    }
    get formattedPriceRange() {
        if (isNaN(this.minPrice) || isNaN(this.maxPrice))
            return "N/A";
        if (this.minPrice === this.maxPrice) {
            return this.formattedMinPrice;
        }
        return `${this.formattedMinPrice}–${this.formattedMaxPrice}`;
    }
    get formattedStockRange() {
        if (isNaN(this.minStock) || isNaN(this.maxStock))
            return "N/A";
        if (this.minStock === this.maxStock) {
            return this.formattedMinStock;
        }
        return `${this.formattedMinStock}–${this.formattedMaxStock}`;
    }
    get lifeCycleStatus() {
        return normalizeLifecycleStatus(this.data.lifeCycleStatus);
    }
    get humanizedLifeCycleStatus() {
        return humanizeLifecycleStatus(this.lifeCycleStatus);
    }
    get minPrice() {
        const min = this.data.priceRange.min ?? NaN;
        if (min >= 0) {
            return min;
        }
        else {
            return 0;
        }
    }
    get formattedMinPrice() {
        return formatPartPrice(this.minPrice);
    }
    get maxPrice() {
        const max = this.data.priceRange.max ?? NaN;
        if (max >= 0) {
            return max;
        }
        else {
            return 0;
        }
    }
    get formattedMaxPrice() {
        return formatPartPrice(this.maxPrice);
    }
    get minStock() {
        const min = this.data.stockRange.min ?? NaN;
        if (min >= 0) {
            return min;
        }
        else {
            return 0;
        }
    }
    get formattedMinStock() {
        return compactNumberFormatter.format(this.minStock);
    }
    get maxStock() {
        const max = this.data.stockRange.max ?? NaN;
        if (max >= 0) {
            return max;
        }
        else {
            return 0;
        }
    }
    get formattedMaxStock() {
        return compactNumberFormatter.format(this.maxStock);
    }
    get skuCount() {
        return this.data.skuCount ?? 0;
    }
    get listingUrl() {
        return this.data.listingUrl ?? null;
    }
    get distributorName() {
        return DistributorInfo[this.distributorIdentifier]?.name ?? capitalize(this.distributorIdentifier);
    }
}
__decorate([
    memoized
], DistributorPriceSummary.prototype, "shortPriceSummary", null);
__decorate([
    memoized
], DistributorPriceSummary.prototype, "shortStockSummary", null);
__decorate([
    memoized
], DistributorPriceSummary.prototype, "formattedPriceRange", null);
__decorate([
    memoized
], DistributorPriceSummary.prototype, "formattedStockRange", null);
//# sourceMappingURL=mpn.js.map